import { Injectable, inject } from '@angular/core';

import type { LooseAutocomplete, Timestamp } from '@b2b/types/core';

import { FILTER_PORT, type IFilterPort } from '../utils/filter.port';
import { USER_PREFERENCES_PORT, type IUserPreferencesPort } from './user-preferences.port';

export type CalendarRangeType =
  | 'today'
  | 'yesterday'
  | 'last_week'
  | 'current_week'
  | 'week_to_date'
  | 'last_month'
  | 'current_month'
  | 'month_to_date'
  | 'last_year'
  | 'current_year'
  | 'ytd';

export interface CalendarRangeOption {
  label: string;
  type: CalendarRangeType;
  dates: string;
  startDate: Timestamp;
  endDate: Timestamp;
  relabel: (start: Date, end: Date) => void;
  logic: (date: Date) => { startDate: Date; endDate: Date };
}

export interface CalendarCurrent {
  defaultView: LooseAutocomplete<'defaultView'>;
  startTimeTracker: Date;
  eventsStartTimeTracker: Date | string | null;
  [key: string]: unknown;
}

export type WeekdayShort = 'Su' | 'M' | 'Tu' | 'W' | 'Th' | 'F' | 'Sa';

export interface DurationParts {
  hour?: number | string;
  minutes?: number | string;
}

/**
 * CalendarService (Angular 18).
 *
 * Replaces the AngularJS legacy service at:
 *   apps/b2b-ui/common/services/calendar/calendar.service.js
 *
 * Exposed to AngularJS via `downgradeInjectable` under the same legacy name
 * `'CalendarService'`, so existing consumers in b2b-ui keep working without
 * modification (see ng-downgraded-components.ts).
 */
@Injectable({ providedIn: 'root' })
export class CalendarService {
  private $filter = inject<IFilterPort>(FILTER_PORT);
  private preferences = inject<IUserPreferencesPort>(USER_PREFERENCES_PORT);

  current: CalendarCurrent = {} as CalendarCurrent;

  weekdays: {
    Su: { name: 'Sunday'; number: 1 };
    M: { name: 'Monday'; number: 2 };
    Tu: { name: 'Tuesday'; number: 3 };
    W: { name: 'Wednesday'; number: 4 };
    Th: { name: 'Thursday'; number: 5 };
    F: { name: 'Friday'; number: 6 };
    Sa: { name: 'Saturday'; number: 7 };
  } = {
    Su: { name: 'Sunday', number: 1 },
    M: { name: 'Monday', number: 2 },
    Tu: { name: 'Tuesday', number: 3 },
    W: { name: 'Wednesday', number: 4 },
    Th: { name: 'Thursday', number: 5 },
    F: { name: 'Friday', number: 6 },
    Sa: { name: 'Saturday', number: 7 },
  };

  ranges: CalendarRangeOption[];

  constructor() {
    this.ranges = this.buildRanges();
  }

  init(): boolean {
    return true;
  }

  setDates(target: CalendarRangeOption, startDate: Date | string, endDate: Date | string, type?: 'single' | 'double'): void {
    target.startDate = (startDate instanceof Date ? startDate.toISOString() : startDate) as Timestamp;
    target.endDate = (endDate instanceof Date ? endDate.toISOString() : endDate) as Timestamp;

    const dateFilter = this.$filter('date');
    if (type === 'double') {
      target.dates =
        dateFilter(startDate, this.preferences.getDateFormat('shortDate', 'dateFormat')) +
        ' to ' +
        dateFilter(endDate, this.preferences.getDateFormat('shortDate', 'dateFormat'));
    } else if (type === 'single') {
      target.dates = dateFilter(startDate, this.preferences.getDateFormat('shortDate', 'dateFormat'));
    }
  }

  create(eventData: unknown): void {
    this.current = eventData as CalendarCurrent;
  }

  getWeekdayShortName(longName: string): WeekdayShort | false {
    let r: WeekdayShort | false = false;
    for (const day in this.weekdays) {
      if (Object.prototype.hasOwnProperty.call(this.weekdays, day)) {
        const key = day as WeekdayShort;
        if (this.weekdays[key].name.toLowerCase() === longName.toLowerCase()) {
          r = key;
          break;
        }
      }
    }
    return r;
  }

  getWeekdayArray(type?: 'short' | 'number' | 'long'): Array<string | number> {
    const r: Array<string | number> = [];
    for (const day in this.weekdays) {
      if (Object.prototype.hasOwnProperty.call(this.weekdays, day)) {
        const key = day as WeekdayShort;
        switch (type) {
          case 'short':
            r.push(key);
            break;
          case 'number':
            r.push(this.weekdays[key].number);
            break;
          case 'long':
            r.push(this.weekdays[key].name);
            break;
          default:
            r.push(this.weekdays[key].name);
        }
      }
    }
    return r;
  }

  getWeek(date: Date | number | string, returnText?: boolean): number | string | undefined {
    const d = new Date(date);
    const week = parseInt(String((d.getDate() - 1) / 7), 10) + 1;

    if (returnText) {
      switch (week) {
        case 1:
          return 'first';
        case 2:
          return 'second';
        case 3:
          return 'third';
        case 4:
          return 'fourth';
        case 5:
          return 'fifth';
        default:
          return undefined;
      }
    }
    return week;
  }

  parseDate(date: unknown): Date {
    let d = date as Date | string;
    if (typeof d === 'string') {
      const split = d.split(/[^0-9]/);
      d = new Date(
        Number(split[0]),
        Number(split[1]) - 1,
        Number(split[2]),
        Number(split[3]),
        Number(split[4]),
        Number(split[5]),
      );
    }
    return d as Date;
  }

  daysToString(days: WeekdayShort[] | unknown): string {
    let r: string[] | string = [];
    const parts: string[] = [];
    const ordered: string[] = [];
    const weekdays = this.getWeekdayArray('short') as WeekdayShort[];
    let lastM: string | false = false;
    let sWeekdays = '';

    if (days instanceof Array) {
      for (let i = 0, ii = days.length; i < ii; i++) {
        for (const day in this.weekdays) {
          if (Object.prototype.hasOwnProperty.call(this.weekdays, day) && days[i] === day) {
            ordered.splice(this.weekdays[day as WeekdayShort].number - 1, 0, days[i]);
            break;
          }
        }
      }

      const sOrdered = ordered.join('');

      for (let i = 0, ii = weekdays.length; i < ii; i++) {
        if (lastM) {
          sWeekdays += weekdays[i];
        } else {
          sWeekdays = weekdays[i];
        }

        const wTest = new RegExp(sWeekdays);
        const m = sOrdered.match(wTest);

        if (m) {
          lastM = m[0];
          if (i === ii - 1) {
            parts.push(m[0]);
          }
        } else {
          if (lastM) {
            parts.push(lastM);
            lastM = false;
          }
        }
      }

      for (let i = 0, ii = parts.length; i < ii; i++) {
        const matches = /^([ST]{1}[auh]{1}|[MTWF]{1})(.*?)([ST]{1}[auh]{1}|[MTWF]{1})$/g.exec(parts[i]);
        if (matches) {
          if (matches[2].length) {
            (r as string[]).push(matches[1] + ' - ' + matches[3]);
          } else {
            (r as string[]).push(matches[1]);
            (r as string[]).push(matches[3]);
          }
        } else {
          (r as string[]).push(parts[i]);
        }
      }

      if ((r as string[]).length) {
        r = (r as string[]).join(', ');
      } else {
        r = 'None';
      }
    } else {
      r = 'None';
    }

    return r as string;
  }

  toISO8601(date: Date | number | string, fixZ?: boolean): Timestamp {
    const format = 'yyyy-MM-ddTHH:mm:ssZ';
    let r = this.$filter('date')(date, format);

    if (fixZ) {
      r = r.replace(/^(.*?)(\+|-)([\d]{2})([\d]{2})$/, '$1$2$3:$4');
    }

    return r as Timestamp;
  }

  toTZISO8601(date: Date | number | string, timezone?: string): Timestamp {
    const format = 'yyyy-MM-ddTHH:mm:ss';
    let r = this.$filter('date')(date, format);

    if (timezone) {
      r += timezone;
    }

    return r as Timestamp;
  }

  toFormat(date: Date | number | string, format: string): string {
    return this.$filter('date')(date, format);
  }

  toYMDDate(date: Date | number | string): string {
    return this.$filter('date')(date, 'yyyy-MM-dd');
  }

  toMinutes(minute: number): string | number {
    if (minute < 10) {
      return '0' + minute;
    }
    return minute;
  }

  toHour(hour: number): number {
    let h = hour;
    if (h > 12) {
      h -= 12;
    }
    if (h === 0) {
      h = 12;
    }
    return h;
  }

  toHourHH(hour: number): string | number {
    let h: string | number = hour;
    if ((h as number) < 10) {
      h = '0' + h;
    }
    if ((h as number) === 0) {
      h = '00';
    }
    return h;
  }

  timeToDurationString(time: DurationParts, showZeroMins?: boolean): string {
    let s = '';
    const h = time.hour ? this.toHour(time.hour as number) : 0;
    const m = time.minutes ? this.toMinutes(time.minutes as number) : '00';

    if (h) {
      s += h + ' hr';
    }
    if (m !== '00' || (showZeroMins && m === '00')) {
      s += ' ' + m + ' min';
    }
    return s;
  }

  durationToString(time: DurationParts, showZeroMins?: boolean): string {
    let s = '';
    const h = time.hour ? time.hour : 0;
    const m = time.minutes ? time.minutes : '00';

    if (h) {
      s += h + ' hr';
    }
    if (m !== '00' || (showZeroMins && m === '00')) {
      s += ' ' + m + ' min';
    }
    return s;
  }

  timeToString(time: DurationParts, format?: string): string {
    let h: string | number = time.hour ? this.toHour(time.hour as number) : '12';
    const m = time.minutes ? this.toMinutes(time.minutes as number) : '00';
    let r = h + ':' + m + ' ' + this.getMeridian(time.hour as number);

    if (format && format === 'HH:mm') {
      h = this.toHourHH(time.hour as number);
      r = h + ':' + m;
    }
    return r;
  }

  timeToDate(time: string): Date {
    const d = new Date();
    const ts = time.split(':');
    let h = +ts[0];
    const m = +ts[1].split(' ')[0];
    const mer = ts[1].split(' ')[1];

    if (mer && mer.toLowerCase() === 'pm' && h < 12) {
      h += 12;
    } else if (mer && mer.toLowerCase() === 'am' && h === 12) {
      h = 0;
    }

    const r: Date = new Date(d.getFullYear(), d.getMonth(), d.getDate(), h, m, 0, 0);

    return r;
  }

  dateRangeToDuration(startDate: Date, endDate: Date): { hour: number; minutes: number } {
    let diff = endDate.getTime() - startDate.getTime();
    const duration = { hour: 0, minutes: 0 };

    if (!diff && diff < 0) {
      diff = 0;
    }
    duration.hour = Math.floor(diff / 3600000);
    duration.minutes = Math.round(((diff % 86400000) % 3600000) / 60000);
    return duration;
  }

  timeToMilitary(time: string): string {
    const parts = time.split(':');
    let hour = parseInt(parts[0], 10);
    const min = parseInt(parts[1], 10);
    const PM = time.match('PM') ? true : false;
    let sec: string;

    if (PM) {
      hour = 12 + hour;
      sec = parts[2].replace('PM', '');
    } else {
      sec = parts[2].replace('AM', '');
    }

    return (
      (hour < 10 ? '0' + hour : hour) + ':' + (min < 10 ? '0' + min : min) + ':' + sec
    );
  }

  militaryTimeToDate(time: string): Date {
    const d = new Date();
    const ts = time.split(':');
    const h = +ts[0];
    const m = +ts[1].split(' ')[0];

    return new Date(d.getFullYear(), d.getMonth(), d.getDate(), h, m, 0);
  }

  getMeridian(hour: number, lower?: boolean): string {
    const m = hour >= 12 ? 'PM' : 'AM';
    return lower ? m.toLowerCase() : m;
  }

  rangeToTime(range: string, isStandardTime?: boolean): string {
    const hours = range.split('-');
    const start = hours[0];
    const end = hours[1];
    const sHour = isStandardTime ? this.toHour(+start.split(':')[0]) : start.split(':')[0];
    const sMinutes = this.toMinutes(+start.split(':')[1]);
    const eHour = isStandardTime ? this.toHour(+end.split(':')[0]) : end.split(':')[0];
    const eMinutes = this.toMinutes(+end.split(':')[1]);
    const sMedirian = isStandardTime ? this.getMeridian(+start.split(':')[0]) : '';
    const eMedirian = isStandardTime ? this.getMeridian(+end.split(':')[0]) : '';

    return sHour + ':' + sMinutes + ' ' + sMedirian + ' to ' + eHour + ':' + eMinutes + ' ' + eMedirian;
  }

  private buildRanges(): CalendarRangeOption[] {
    const service = this;

    const make = (
      label: string,
      type: CalendarRangeType,
      relabelKind: 'single' | 'double',
      logicKind: 'single' | 'double',
      compute: (date: Date) => { startDate: Date; endDate: Date },
    ): CalendarRangeOption => {
      const option: CalendarRangeOption = {
        label,
        type,
        dates: '',
        startDate: '' as Timestamp,
        endDate: '' as Timestamp,
        relabel(this: CalendarRangeOption, start: Date, end: Date) {
          service.setDates(this, start, end, relabelKind);
          this.label = this.label + ' - ' + this.dates;
        },
        logic(this: CalendarRangeOption, date: Date) {
          const { startDate, endDate } = compute(date);
          service.setDates(this, startDate, endDate, logicKind);
          return { startDate, endDate };
        },
      };
      return option;
    };

    return [
      make('Today', 'today', 'single', 'single', (date) => ({
        startDate: new Date(date.getFullYear(), date.getMonth(), date.getDate(), 0, 0, 0, 0),
        endDate: new Date(date.getFullYear(), date.getMonth(), date.getDate(), 23, 59, 59, 999),
      })),
      make('Yesterday', 'yesterday', 'single', 'single', (date) => ({
        startDate: new Date(date.getFullYear(), date.getMonth(), date.getDate() - 1, 0, 0, 0, 0),
        endDate: new Date(date.getFullYear(), date.getMonth(), date.getDate() - 1, 23, 59, 59, 999),
      })),
      // Note: legacy 'Last Week' relabel called setDates without a type — preserved here.
      (() => {
        const opt = make('Last Week', 'last_week', 'single', 'double', (date) => ({
          startDate: new Date(
            date.getFullYear(),
            date.getMonth(),
            date.getDate() - date.getDay() - 7,
            0,
            0,
            0,
            0,
          ),
          endDate: new Date(
            date.getFullYear(),
            date.getMonth(),
            date.getDate() - date.getDay() - 1,
            23,
            59,
            59,
            999,
          ),
        }));
        opt.relabel = function (this: CalendarRangeOption, start: Date, end: Date) {
          service.setDates(this, start, end);
          this.label = this.label + ' - ' + this.dates;
        };
        return opt;
      })(),
      make('This Week', 'current_week', 'double', 'double', (date) => ({
        startDate: new Date(
          date.getFullYear(),
          date.getMonth(),
          date.getDate() - date.getDay(),
          0,
          0,
          0,
          0,
        ),
        endDate: new Date(
          date.getFullYear(),
          date.getMonth(),
          date.getDate() - date.getDay() + 6,
          23,
          59,
          59,
          999,
        ),
      })),
      make('Week to Date', 'week_to_date', 'double', 'double', (date) => ({
        startDate: new Date(
          date.getFullYear(),
          date.getMonth(),
          date.getDate() - date.getDay(),
          0,
          0,
          0,
          0,
        ),
        endDate: new Date(date.getFullYear(), date.getMonth(), date.getDate(), 23, 59, 59, 999),
      })),
      make('Last Month', 'last_month', 'double', 'double', (date) => ({
        startDate: new Date(date.getFullYear(), date.getMonth() - 1, 1, 0, 0, 0, 0),
        endDate: new Date(date.getFullYear(), date.getMonth(), 0, 23, 59, 59, 999),
      })),
      make('This Month', 'current_month', 'double', 'double', (date) => ({
        startDate: new Date(date.getFullYear(), date.getMonth(), 1, 0, 0, 0, 0),
        endDate: new Date(date.getFullYear(), date.getMonth() + 1, 0, 23, 59, 59, 999),
      })),
      make('Month to Date', 'month_to_date', 'double', 'double', (date) => ({
        startDate: new Date(date.getFullYear(), date.getMonth(), 1, 0, 0, 0, 0),
        endDate: new Date(date.getFullYear(), date.getMonth(), date.getDate(), 23, 59, 59, 999),
      })),
      make('Last Year', 'last_year', 'double', 'double', (date) => ({
        startDate: new Date(date.getFullYear() - 1, 0, 1, 0, 0, 0, 0),
        endDate: new Date(date.getFullYear() - 1, 11, 31, 23, 59, 59, 999),
      })),
      make('This Year', 'current_year', 'double', 'double', (date) => ({
        startDate: new Date(date.getFullYear(), 0, 1, 0, 0, 0, 0),
        endDate: new Date(date.getFullYear(), 11, 31, 23, 59, 59, 999),
      })),
      make('Year to Date', 'ytd', 'double', 'double', (date) => ({
        startDate: new Date(date.getFullYear(), 0, 1, 0, 0, 0, 0),
        endDate: new Date(date.getFullYear(), date.getMonth(), date.getDate(), 23, 59, 59, 999),
      })),
    ];
  }
}
